import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseQuery, handleSessionExpiredError } from '../../utils/auth';
import {
  CreateOfferReqReq,
  GetCreationStepReq,
  CreationStepsReq,
  CreationStepsRes,
  UpdateDraftStep,
  GetCreationStepRes,
  SaveDraftReq,
  GetCreationDepRes,
  GetCreationDepReq,
} from './types';
import { Base } from '../../types/GenericFormTypes';
import { Account } from '../../types/auth';

// Define a service using a base URL and expected endpoints
export const createOfferApi = createApi({
  reducerPath: 'createOfferApi',
  baseQuery: getBaseQuery('/offer-draft-flow'),
  endpoints: (builder) => ({
    getCreationSteps: builder.query<CreationStepsRes, CreationStepsReq>({
      query: (params) => ({
        url: '/creation-steps',
        params,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getCreationStep: builder.query<GetCreationStepRes, GetCreationStepReq>({
      query: ({ id, area }) => {
        let url = '';
        if (area) {
          url += `/${area}`;
        }
        if (id) {
          url += `/${id}`;
        }
        return {
          url,
        };
      },
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getCreationDep: builder.query<GetCreationDepRes, GetCreationDepReq>({
      query: ({ id, dep }) => ({
        url: `/detail/${id}/${dep}`,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    createOfferReq: builder.mutation<Base, CreateOfferReqReq>({
      query: ({ type, body, area }) => ({
        method: 'POST',
        url: `/${area}/${type}`,
        body,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    updateDraftStep: builder.mutation<void, UpdateDraftStep>({
      query: ({ id, step }) => ({
        method: 'PUT',
        url: `/${id}`,
        body: { step },
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    searchUsers: builder.mutation<Account[], string>({
      query: (search) => ({
        url: `/invitation/users?search=${search}`,
        method: 'GET',
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    createDraftId: builder.mutation<number, void>({
      query: () => ({
        method: 'POST',
        url: '',
      }),
      transformResponse: (response: any) => response?.id,
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    saveDraft: builder.mutation<void, SaveDraftReq>({
      query: ({ id, area, payload }) => ({
        url: `/${area}/${id}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: any) => response?.id,
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCreationStepsQuery,
  useGetCreationDepQuery,
  useGetCreationStepQuery,
  useCreateOfferReqMutation,
  useCreateDraftIdMutation,
  useSearchUsersMutation,
  useUpdateDraftStepMutation,
  useSaveDraftMutation,
} = createOfferApi;
